/**
 * RunCreateDialog — Dialog for creating a new training run with scheduling support
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
  Typography,
  Alert,
  CircularProgress,
  Snackbar,
  MenuItem,
  Box,
  Checkbox,
  Switch,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  MyLocation as MyLocationIcon,
} from '@mui/icons-material';
import { formatDateSafe } from '../utils/dateFormat';
import { runsApi, networkApi, extractErrorMessage } from '../services/api';
import { DEFAULT_RDP_SOURCE_CIDR, parseRdpSourceCidrList } from '../utils/rdpSourceCidr';
import { MY_IP_LABEL } from './RunRdpAccessPanel';
import { workloadsApi } from '../services/workloadsService';
import { roomsApi } from '../services/roomsService';
import { costCentersApi } from '../services/costCentersService';
import { WorkloadDefinition, SchulungsraumDefinition, CostCenterDefinition } from '../services/types';
import {
  toApiDateOrIso,
  nowLocalIso,
  endFromDuration,
  parseDurationHours,
  isNightEnd,
  isEndAfterStart,
  QUICK_DURATION_PRESETS,
} from './dateInputHelpers';
import { useNavigate } from 'react-router-dom';

interface Participant {
  firstName: string;
  lastName: string;
  // NC-1074: optional contact metadata the tutor captures at creation.
  externalEmail: string;
  company: string;
}

interface RunCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  /** NC-1567: admin only — show the "Über Nacht laufen lassen" switch. */
  canKeepRunning?: boolean;
}

const emptyParticipant = (): Participant => ({ firstName: '', lastName: '', externalEmail: '', company: '' });

export default function RunCreateDialog({ open, onClose, onCreated, canKeepRunning = false }: RunCreateDialogProps) {
  const navigate = useNavigate();
  const [workloadId, setWorkloadId] = useState('');
  const [roomId, setRoomId] = useState('');
  const [costCenter, setCostCenter] = useState('');
  const [course, setCourse] = useState('');
  const [scheduledStart, setScheduledStart] = useState('');
  const [expireOn, setExpireOn] = useState('');
  const [participants, setParticipants] = useState<Participant[]>([emptyParticipant()]);
  // NC-1173 (#1164-S2b): opt-in public RDP + its source-CIDR whitelist.
  const [publicRdp, setPublicRdp] = useState(false);
  const [rdpCidr, setRdpCidr] = useState('');
  // NC-1579: the field holds a list; parsed once per render for payload + validity.
  const rdpCidrCheck = parseRdpSourceCidrList(rdpCidr);
  const [myIpLoading, setMyIpLoading] = useState(false);
  const [myIpError, setMyIpError] = useState<string | null>(null);

  // NC-1579: "Meine aktuelle IP übernehmen" — append the caller's /32 to the list.
  const handleMyIp = async () => {
    setMyIpLoading(true);
    setMyIpError(null);
    try {
      const { cidr } = await networkApi.getClientIp();
      if (!rdpCidrCheck.cidrs.includes(cidr)) {
        setRdpCidr((prev) => (prev.trim() ? `${prev.trim()}, ${cidr}` : cidr));
      }
    } catch (err) {
      setMyIpError(extractErrorMessage(err));
    } finally {
      setMyIpLoading(false);
    }
  };
  // NC-1168 (Slice 3): opt-in "Testlauf: direkt nach Ende aufräumen".
  const [fastCleanup, setFastCleanup] = useState(false);
  // NC-1567: admin opt-out from the 19:00 evening shutdown (KeepRunning tag).
  const [keepRunning, setKeepRunning] = useState(false);

  // NC-1287: quick mode = "jetzt starten fuer N Stunden". Detail mode is the
  // default so the dialog behaves exactly as before for anyone who does not
  // opt in; the toggle is one click away for the spontaneous-session case the
  // beta feedback asks for.
  // NC-1288: reporting classification. Deliberately NOT coupled to fastCleanup —
  // the issue is explicit that fastCleanup stays a retention opt-in while this is
  // reporting semantics. A quick run is therefore not automatically a test run.
  const [isTestRun, setIsTestRun] = useState(false);
  const [mode, setMode] = useState<'quick' | 'detail'>('detail');
  const [quickPreset, setQuickPreset] = useState<number | 'custom'>(2);
  const [quickCustomHours, setQuickCustomHours] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'warning';
    // NC-1236: runId of the just-created run → drives the "Live-Status ansehen"
    // toast action so the tutor lands on the run's live status instead of waiting.
    runId?: string;
  }>({ open: false, message: '', severity: 'success' });
  const [workloads, setWorkloads] = useState<WorkloadDefinition[]>([]);
  const [workloadsLoading, setWorkloadsLoading] = useState(false);
  const [rooms, setRooms] = useState<SchulungsraumDefinition[]>([]);
  const [costCenters, setCostCenters] = useState<CostCenterDefinition[]>([]);

  useEffect(() => {
    if (open) {
      setWorkloadsLoading(true);
      workloadsApi
        .list()
        .then((data) => setWorkloads(data))
        .catch(() => setWorkloads([]))
        .finally(() => setWorkloadsLoading(false));
      // NC-798: optional room dropdown — only active rooms are selectable.
      // A fetch failure leaves the dropdown empty ("Kein Raum" only) so the
      // dialog stays fully usable even if the rooms endpoint is unavailable.
      roomsApi
        .list({ isActive: true })
        .then((data) => setRooms(data))
        .catch(() => setRooms([]));
      // NC-867 T4: mandatory Kostenstelle. Load the active cost-centers; when
      // exactly one exists (current reality: IT-Plattform) pre-select it so the
      // operator isn't forced to pick from a list of one. With multiple active
      // cost-centers no default is set — an explicit choice is required.
      costCentersApi
        .list({ active: true })
        .then((data) => {
          setCostCenters(data);
          setCostCenter((prev) => (prev === '' && data.length === 1 ? data[0].code : prev));
        })
        .catch(() => setCostCenters([]));
    }
  }, [open]);

  const resetForm = () => {
    setWorkloadId('');
    setRoomId('');
    setCostCenter('');
    setCourse('');
    setScheduledStart('');
    setExpireOn('');
    setParticipants([emptyParticipant()]);
    setPublicRdp(false);
    setRdpCidr('');
    setMyIpError(null);
    setFastCleanup(false);
    setKeepRunning(false);
    setIsTestRun(false);
    setMode('detail');
    setQuickPreset(2);
    setQuickCustomHours('');
    setError(null);
  };

  const handleClose = () => {
    if (!submitting) {
      resetForm();
      onClose();
    }
  };

  const handleAddParticipant = () => {
    setParticipants((prev) => [...prev, emptyParticipant()]);
  };

  const handleRemoveParticipant = (index: number) => {
    setParticipants((prev) => prev.filter((_, i) => i !== index));
  };

  const handleParticipantChange = (
    index: number,
    field: keyof Participant,
    value: string
  ) => {
    setParticipants((prev) =>
      prev.map((p, i) => (i === index ? { ...p, [field]: value } : p))
    );
  };

  // NC-1287: the quick duration in hours, or null when the custom field holds
  // something unusable (empty, 0, negative, non-numeric, > 14 days).
  const quickHours: number | null =
    quickPreset === 'custom' ? parseDurationHours(quickCustomHours) : quickPreset;

  // Derived on every render rather than memoised: a memo with an empty dep list
  // would freeze "now" at mount, and a dialog left open would then submit an end
  // time in the past. The submit path recomputes from the duration for the same
  // reason — the user's intent is "N hours from now", not a fixed instant.
  const quickEnd: string = quickHours !== null ? endFromDuration(quickHours) : '';

  const handleModeChange = (next: 'quick' | 'detail' | null) => {
    if (!next || next === mode) return;
    setMode(next);
    // AC 3: fastCleanup is pre-selected in quick mode (a spontaneous 2 h session
    // is a test run) but stays de-selectable, and switching back does not undo a
    // deliberate choice.
    if (next === 'quick') setFastCleanup(true);
  };

  const isExpireOnValid = (): boolean => {
    if (!expireOn) return false;
    const expireDate = new Date(expireOn);
    if (isNaN(expireDate.getTime())) return false;
    // NC-457: datetime values compare against now (allows short same-day courses);
    // legacy date-only values still compare against today midnight.
    const cutoff = new Date();
    if (!expireOn.includes('T')) {
      cutoff.setHours(0, 0, 0, 0);
    }
    return expireDate > cutoff;
  };

  const hasValidParticipants = (): boolean => {
    return (
      participants.length > 0 &&
      participants.every(
        (p) => p.firstName.trim() !== '' && p.lastName.trim() !== ''
      )
    );
  };

  const isFormValid = (): boolean => {
    return (
      workloadId !== '' &&
      costCenter !== '' &&
      course.trim() !== '' &&
      // NC-1287: quick mode derives the end from the duration, so validity is
      // "is the duration usable" (AC 6d); detail mode keeps the field checks and
      // additionally enforces end > start, which was never checked before (AC 5).
      (mode === 'quick'
        ? quickHours !== null
        : isExpireOnValid() && isEndAfterStart(scheduledStart, expireOn)) &&
      hasValidParticipants() &&
      // NC-1579: a typo in the IP list must not reach the API as a 400.
      (!publicRdp || !rdpCidrCheck.error)
    );
  };

  const handleSubmit = async () => {
    if (!isFormValid()) return;

    setSubmitting(true);
    setError(null);

    try {
      // NC-1287: quick runs carry no scheduledStart (that is what puts them in
      // status 'provisioning' and triggers the immediate dispatch below, NC-778)
      // and their end is recomputed here so it is measured from the submit, not
      // from whenever the dialog happened to be opened.
      const isQuick = mode === 'quick';
      const expireOnValue = isQuick
        ? endFromDuration(quickHours as number)
        : expireOn;
      const scheduledStartPayload =
        !isQuick && scheduledStart ? toApiDateOrIso(scheduledStart) : undefined;

      const created = await runsApi.create({
        workloadId,
        costCenter,
        course: course.trim(),
        expireOn: toApiDateOrIso(expireOnValue),
        scheduledStart: scheduledStartPayload,
        // NC-798: include the optional room only when one is selected.
        ...(roomId ? { roomId } : {}),
        // NC-1173 (#1164-S2b): include public-RDP access only when opted in.
        // An empty CIDR field is sent as an empty list — the API defaults it to
        // the company egress /32 (never any-source). NC-1579: the field takes a
        // list (comma/space separated); bare addresses are completed to /32.
        ...(publicRdp
          ? {
              access: {
                publicRdp: true,
                allowedSourceCidrs: rdpCidrCheck.cidrs,
              },
            }
          : {}),
        // NC-1168 (Slice 3): only send the opt-in when ticked, so an untouched
        // dialog produces the exact payload it did before (retention default).
        ...(fastCleanup ? { fastCleanup: true } : {}),
        // NC-1567: only for admins and only when switched on — an operator's
        // payload is byte-identical to before (the API would 403 it anyway).
        ...(canKeepRunning && keepRunning ? { keepRunning: true } : {}),
        // NC-1288: send the classification only when the operator ticked it, so an
        // untouched dialog produces the exact payload it did before. The API
        // defaults an absent value to 'produktiv'.
        ...(isTestRun ? { classification: 'test' as const } : {}),
        participants: participants.map((p) => {
          const externalEmail = p.externalEmail.trim();
          const company = p.company.trim();
          return {
            firstName: p.firstName.trim(),
            lastName: p.lastName.trim(),
            // NC-1074: only send when filled, so the payload stays minimal.
            ...(externalEmail ? { externalEmail } : {}),
            ...(company ? { company } : {}),
          };
        }),
      });

      // NC-778 (D6): runs without scheduledStart land in status 'provisioning',
      // but nothing dispatched the workflow — trigger it here. A dispatch
      // failure must NOT invalidate the create: warn and let the operator
      // retry from the details page. scheduledStart runs (status 'planned')
      // stay untouched — the missing scheduler is #642.
      let snackbarMessage = 'Schulung erfolgreich erstellt';
      let snackbarSeverity: 'success' | 'warning' = 'success';
      if (created.status === 'provisioning') {
        try {
          await runsApi.provision(created.runId);
          snackbarMessage = 'Schulung erstellt — Provisionierung gestartet';
        } catch {
          snackbarMessage =
            'Schulung angelegt, Provisionierung konnte nicht gestartet werden — auf der Detailseite erneut starten';
          snackbarSeverity = 'warning';
        }
      }

      setSnackbar({ open: true, message: snackbarMessage, severity: snackbarSeverity, runId: created.runId });
      resetForm();
      onCreated();
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Fehler beim Erstellen der Schulung'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // NC-457: min value for datetime-local inputs (browser local time, "yyyy-MM-ddTHH:mm").
  const minValue = nowLocalIso();

  // NC-798: non-blocking capacity warning. The server does NOT validate capacity,
  // so this is purely a heads-up — submit stays enabled even when exceeded.
  const selectedRoom = roomId ? rooms.find((r) => r.id === roomId) : undefined;
  const capacityExceeded =
    selectedRoom !== undefined && participants.length > selectedRoom.capacity;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Neue Schulung erstellen</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Workload"
                value={workloadId}
                onChange={(e) => setWorkloadId(e.target.value)}
                required
                disabled={workloadsLoading}
                InputProps={{
                  endAdornment: workloadsLoading ? (
                    <CircularProgress size={20} sx={{ mr: 2 }} />
                  ) : undefined,
                }}
              >
                {workloads.length > 0
                  ? workloads.map((w) => (
                      // NC-1273: a workload that cannot be provisioned yet is shown
                      // DISABLED with the reason, not hidden. Hiding it would leave
                      // the tutor wondering where their workload went; picking it
                      // used to end in a 400 from runs-provision at the very last
                      // step. `isProvisionable === false` is the only blocking
                      // case — undefined means an older API response and must stay
                      // selectable.
                      <MenuItem
                        key={w.id}
                        value={w.id}
                        disabled={w.isProvisionable === false}
                      >
                        {w.isProvisionable === false
                          ? `${w.name} — noch nicht bereitstellbar`
                          : w.name}
                      </MenuItem>
                    ))
                  : [
                      // Degraded path: the workloads request failed, so there is no
                      // authoritative list to derive provisionability from. Both
                      // entries below are on PROVISIONABLE_WORKLOADS, so this
                      // fallback can never offer something unprovisionable — do not
                      // extend it without checking that list.
                      <MenuItem key="cam" value="cam">
                        CAM
                      </MenuItem>,
                      <MenuItem key="bim" value="bim">
                        BIM
                      </MenuItem>,
                    ]}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Kostenstelle"
                value={costCenter}
                onChange={(e) => setCostCenter(e.target.value)}
                required
                helperText={
                  costCenters.length === 0
                    ? 'Keine aktive Kostenstelle verfügbar — bitte zuerst eine Kostenstelle anlegen'
                    : 'Pflichtfeld — jede Schulung wird einer Kostenstelle zugeordnet'
                }
              >
                {costCenters.map((cc) => (
                  <MenuItem key={cc.code} value={cc.code}>
                    {cc.displayName} ({cc.code})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Schulungsraum (optional)"
                value={roomId}
                onChange={(e) => setRoomId(e.target.value)}
                helperText="Optionaler Raum für diese Schulung"
              >
                <MenuItem value="">Kein Raum</MenuItem>
                {rooms.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name} ({r.location})
                  </MenuItem>
                ))}
              </TextField>
              {capacityExceeded && selectedRoom && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Der Raum „{selectedRoom.name}" hat {selectedRoom.capacity}{' '}
                  {selectedRoom.capacity === 1 ? 'Arbeitsplatz' : 'Arbeitsplätze'} —
                  {' '}aktuell sind {participants.length} Teilnehmer eingetragen.
                </Alert>
              )}
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Kursname"
                value={course}
                onChange={(e) => setCourse(e.target.value)}
                required
              />
            </Grid>

            {/* NC-1287: Quick vs. Detail. Quick hides both datetime fields and
                derives the end from a duration; Detail is the unchanged form. */}
            <Grid item xs={12}>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={mode}
                onChange={(_e: unknown, next: 'quick' | 'detail' | null) => handleModeChange(next)}
                aria-label="Zeitplanung"
              >
                <ToggleButton value="quick" aria-label="Schnellbereitstellung">
                  Jetzt starten
                </ToggleButton>
                <ToggleButton value="detail" aria-label="Zeitraum planen">
                  Zeitraum planen
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>

            {mode === 'quick' ? (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Jetzt starten für …
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={quickPreset}
                    onChange={(_e: unknown, next: number | 'custom' | null) =>
                      next !== null && setQuickPreset(next)
                    }
                    aria-label="Dauer waehlen"
                  >
                    {QUICK_DURATION_PRESETS.map((hours) => (
                      <ToggleButton key={hours} value={hours} aria-label={`${hours} Stunden`}>
                        {hours} h
                      </ToggleButton>
                    ))}
                    <ToggleButton value="custom" aria-label="Eigene Dauer">
                      Eigene Dauer
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>

                {quickPreset === 'custom' && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Dauer (Stunden)"
                      value={quickCustomHours}
                      onChange={(e) => setQuickCustomHours(e.target.value)}
                      required
                      error={quickCustomHours !== '' && quickHours === null}
                      helperText={
                        quickCustomHours !== '' && quickHours === null
                          ? 'Dauer muss größer als 0 und höchstens 336 Stunden (14 Tage) sein'
                          : 'z. B. 1,5 für 90 Minuten'
                      }
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  {/* AC 2: show the resulting end before submitting. */}
                  <Typography variant="body2" data-testid="quick-end-preview">
                    {quickHours !== null
                      ? `Start sofort, Ende ${formatDateSafe(toApiDateOrIso(quickEnd))} (${quickHours} h)`
                      : 'Bitte eine Dauer angeben.'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Die Provisionierung startet direkt nach dem Anlegen. Das automatische
                    Stoppen am Laufende hängt noch am Sweep-Scheduler (#1168 Phase 2) —
                    bis dahin stoppt der Lauf über das 19:00-Runbook oder manuell.
                  </Typography>
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Geplanter Start"
                    type="datetime-local"
                    value={scheduledStart}
                    onChange={(e) => setScheduledStart(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: minValue }}
                    helperText="Lokale Zeit (Browser-Zeitzone)"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Enddatum"
                    type="datetime-local"
                    value={expireOn}
                    onChange={(e) => setExpireOn(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: minValue }}
                    required
                    error={
                      expireOn !== '' &&
                      (!isExpireOnValid() || !isEndAfterStart(scheduledStart, expireOn))
                    }
                    helperText={
                      expireOn !== '' && !isExpireOnValid()
                        ? 'Enddatum muss in der Zukunft liegen'
                        : expireOn !== '' && !isEndAfterStart(scheduledStart, expireOn)
                          ? 'Ende muss nach dem geplanten Start liegen'
                          : 'Lokale Zeit (Browser-Zeitzone)'
                    }
                  />
                </Grid>

                {/* AC 4: soft warning only — night end times stay selectable. */}
                {isNightEnd(expireOn) && (
                  <Grid item xs={12}>
                    <Alert severity="info" data-testid="night-end-warning">
                      Endzeit liegt in den Nachtstunden (00:00–08:00) — beabsichtigt?
                    </Alert>
                  </Grid>
                )}
              </>
            )}

            {/* NC-1589: every run gets a tutor account (Epic #1586) — say so, so
                tutors stop adding themselves as participant just to get admin. */}
            <Grid item xs={12}>
              {/* Plain text, not an Alert: a static hint must not claim role="alert". */}
              <Typography variant="body2" color="text.secondary">
                Du erhältst automatisch ein Tutor-Konto mit Administrator-Rechten auf allen Teilnehmer-VMs —
                du musst dich dafür nicht als Teilnehmer eintragen. Die Zugangsdaten stehen danach auf der Schulungsseite.
              </Typography>
            </Grid>

            {/* NC-1173 (#1164-S2b): opt-in public RDP + source-CIDR whitelist. */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={publicRdp}
                    onChange={(e) => setPublicRdp(e.target.checked)}
                  />
                }
                label="Öffentlicher RDP-Zugang (Teilnehmer verbinden sich direkt per RDP)"
              />
              {/* NC-1404: the default stays off, but the consequence must be visible —
                  a run created without the switch has no RDP endpoint at all. */}
              {!publicRdp && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                  Ohne Haken erhalten die VMs nur eine interne Adresse — Tutor und Teilnehmer können sie dann nicht per RDP erreichen.
                </Typography>
              )}
              {publicRdp && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Erlaubte Quell-IPs"
                    value={rdpCidr}
                    onChange={(e) => setRdpCidr(e.target.value)}
                    placeholder={DEFAULT_RDP_SOURCE_CIDR}
                    error={!!rdpCidrCheck.error}
                    helperText={
                      rdpCidrCheck.error ??
                      `Mehrere durch Komma trennen. Leer lassen für den Firmen-Standard (${DEFAULT_RDP_SOURCE_CIDR}). Nachträglich in der Schulung änderbar.`
                    }
                  />
                  <Tooltip title={MY_IP_LABEL}>
                    <span>
                      <IconButton aria-label={MY_IP_LABEL} onClick={handleMyIp} disabled={myIpLoading}>
                        {myIpLoading ? <CircularProgress size={18} /> : <MyLocationIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              )}
              {publicRdp && myIpError && (
                <Typography variant="caption" color="error" display="block">
                  {myIpError}
                </Typography>
              )}
            </Grid>

            {/* NC-1168 (Slice 3): opt-in "direkt nach Ende aufräumen". */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={fastCleanup}
                    onChange={(e) => setFastCleanup(e.target.checked)}
                  />
                }
                label="Testlauf: direkt nach Ende aufräumen"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                {fastCleanup
                  ? 'Projektdateien werden eingesammelt und der Lauf wird kurz nach dem Ende gelöscht — keine Aufbewahrungsfrist.'
                  : 'Ohne Haken bleibt der Lauf bis zum Ablaufdatum bestehen und wird erst vom täglichen Sweep aufgeräumt.'}
              </Typography>
            </Grid>

            {/* NC-1567: admin-only opt-out from the 19:00 evening shutdown. */}
            {canKeepRunning && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={keepRunning}
                      onChange={(e) => setKeepRunning(e.target.checked)}
                    />
                  }
                  label="Über Nacht laufen lassen (KeepRunning)"
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                  {keepRunning
                    ? 'Die VMs werden vom Abend-Shutdown (19:00) ausgenommen und laufen durch — verursacht Kosten über Nacht.'
                    : 'Ohne Schalter werden die VMs wie üblich um 19:00 heruntergefahren.'}
                </Typography>
              </Grid>
            )}

            {/* NC-1288: reporting classification — separate from fastCleanup above.
                fastCleanup answers "wie lange bleibt der Lauf liegen?", this one
                answers "zaehlt der Lauf als echte Schulung?". */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isTestRun}
                    onChange={(e) => setIsTestRun(e.target.checked)}
                  />
                }
                label="Als Testlauf kennzeichnen (nicht als echte Schulung zaehlen)"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                {isTestRun
                  ? 'Der Lauf wird im Portal als Test markiert und laesst sich in der Liste herausfiltern.'
                  : 'Ohne Haken gilt der Lauf als produktive Schulung.'}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2">Teilnehmer</Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddParticipant}
                >
                  Teilnehmer hinzufuegen
                </Button>
              </Box>

              {participants.map((participant, index) => (
                <Grid container spacing={1} key={index} sx={{ mb: 1 }}>
                  <Grid item xs={5}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Vorname"
                      value={participant.firstName}
                      onChange={(e) =>
                        handleParticipantChange(index, 'firstName', e.target.value)
                      }
                      required
                    />
                  </Grid>
                  <Grid item xs={5}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Nachname"
                      value={participant.lastName}
                      onChange={(e) =>
                        handleParticipantChange(index, 'lastName', e.target.value)
                      }
                      required
                    />
                  </Grid>
                  <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveParticipant(index)}
                      disabled={participants.length <= 1}
                      aria-label="Teilnehmer entfernen"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Grid>
                  {/* NC-1074: optional contact metadata — tutors capture the
                      external e-mail + company already at creation. */}
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Firma (optional)"
                      value={participant.company}
                      onChange={(e) =>
                        handleParticipantChange(index, 'company', e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="email"
                      label="Email extern (optional)"
                      value={participant.externalEmail}
                      onChange={(e) =>
                        handleParticipantChange(index, 'externalEmail', e.target.value)
                      }
                    />
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Abbrechen
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!isFormValid() || submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : undefined}
          >
            {submitting ? 'Erstelle...' : 'Schulung erstellen'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.runId ? 12000 : 6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          variant="filled"
          action={
            snackbar.runId ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  const id = snackbar.runId;
                  setSnackbar((prev) => ({ ...prev, open: false }));
                  if (id) navigate(`/runs/${id}`);
                }}
              >
                Live-Status ansehen
              </Button>
            ) : undefined
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
